import React, { useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  Collapse,
  useTheme,
  Grid,
} from "@mui/material";
import { getFormSectionStyles } from "../formSectionStyles";
import DynamicServiceForm from "../DynamicServiceForm";
import { getStepDefinitionMap, getStepDefinitions } from "../stepDefinitions";

const STORAGE_STEPS = getStepDefinitions("storage");
const STORAGE_STEP_MAP = getStepDefinitionMap("storage");
const SERVICE_OPTIONS = STORAGE_STEPS.map((s) => s.id);

interface AssetStorageFormProps {
  config: any;
  handleChange: (path: string, value: any) => void;
}

const AssetStorageForm: React.FC<AssetStorageFormProps> = ({
  config,
  handleChange,
}) => {
  const theme = useTheme();
  const styles = getFormSectionStyles(theme);
  const cachedConfigsRef = useRef<Record<string, any>>({});
  const disabledServicesRef = useRef<Set<string>>(new Set());

  const enabledServices = useMemo(() => {
    return SERVICE_OPTIONS.filter(
      (s) => config[`assetStorage_${s}_serviceConfig`]
    );
  }, [config]);

  const toggleService = (service: string, enabled: boolean) => {
    const serviceKey = `assetStorage_${service}_serviceConfig`;
    const definition = STORAGE_STEP_MAP[service];

    if (enabled) {
      disabledServicesRef.current.delete(service);
      const restored =
        cachedConfigsRef.current[service] ||
        structuredClone(definition?.defaults || {});
      handleChange(serviceKey, restored);
      cachedConfigsRef.current[service] = restored;
    } else {
      disabledServicesRef.current.add(service);
      cachedConfigsRef.current[service] = config[serviceKey];
      handleChange(serviceKey, undefined);
    }
  };

  return (
    <Paper sx={styles.paper}>
      <Typography variant="h6" sx={styles.title}>
        Asset Storage Services
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {SERVICE_OPTIONS.map((service) => {
          const enabled = enabledServices.includes(service);
          const definition = STORAGE_STEP_MAP[service];
          return (
            <Grid item key={service}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enabled}
                    onChange={(e) => toggleService(service, e.target.checked)}
                    sx={styles.checkbox}
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 500 }}>
                    {definition?.label ||
                      service.charAt(0).toUpperCase() + service.slice(1)}
                  </Typography>
                }
              />
            </Grid>
          );
        })}
      </Grid>

      {SERVICE_OPTIONS.map((service) => {
        const definition = STORAGE_STEP_MAP[service];
        const serviceKey = `assetStorage_${service}_serviceConfig`;
        const enabled = enabledServices.includes(service);
        return (
          <Collapse key={service} in={enabled} unmountOnExit>
            <Box sx={styles.collapsibleRail}>
              {definition?.renderer === "dynamic" ? (
                <DynamicServiceForm
                  definition={definition}
                  config={config[serviceKey] || {}}
                  onChange={(field: string, value: any) => {
                    if (disabledServicesRef.current.has(service)) return;
                    handleChange(`${serviceKey}.${field}`, value);
                  }}
                />
              ) : null}
            </Box>
          </Collapse>
        );
      })}
    </Paper>
  );
};

export default AssetStorageForm;
